import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	formatPhoneNumber,
	getDialFailureAction,
	getInitialRingOutPhone,
	usePreferencesStore,
} from '@supply-mover-2-0/core';
import type { RingCentralDialResponseDto } from '@supply-mover-2-0/shared';
import { Capabilities, CapabilityService } from '@supply-mover-2-0/shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useReliablePbxSafe } from '../../providers/ReliablePbxProvider';
import { useRingCentralSafe } from '../../providers/RingCentralProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { useVonageSafe } from '../../providers/VonageProvider';
import { Toast } from '../../services/ToastService';
import { useSoftphoneSafe } from '../../softphone/hooks/useSoftphoneSafe';
import { SoftphoneSelfServiceQueryService } from '../../softphone/services/SoftphoneSelfServiceQueryService';
import type { SoftphoneDialCrmContextModel } from '../../softphone/SoftphoneProvider';
import type { CallMethodIntegration, CallMethodType } from './CallMethodModal';
import { CallMethodModal } from './CallMethodModal';
import type { RingCentralDialResult } from './RingCentralDialModal';
import { RingCentralDialModal } from './RingCentralDialModal';
import { RingCentralUserAuthModal } from './RingCentralUserAuthModal';
import { VonageExtensionSetupModal } from './VonageExtensionSetupModal';

type RingCentralCallMethodPreference = 'ringcentral' | 'phone_app' | null;
type DialMethodPreference = 'callout' | 'ringout' | null;

// ========================================
// RingCentral store preference functions
// ========================================

function getRingCentralCallMethodPreference(): RingCentralCallMethodPreference {
	const stored =
		usePreferencesStore.getState().ringCentral.callMethodPreference;

	if (stored === 'ringcentral' || stored === 'phone_app') {
		return stored;
	}

	return null;
}

function setRingCentralCallMethodPreference(
	preference: RingCentralCallMethodPreference,
): void {
	usePreferencesStore
		.getState()
		.setRingCentralPreference('callMethodPreference', preference);
}

function getDialMethodPreference(): DialMethodPreference {
	const stored = usePreferencesStore.getState().ringCentral.dialMethod;

	if (stored === 'callout' || stored === 'ringout') {
		return stored;
	}

	return null;
}

function setDialMethodPreference(preference: DialMethodPreference): void {
	usePreferencesStore
		.getState()
		.setRingCentralPreference('dialMethod', preference);
}

interface CallOutDevicePreference {
	deviceId: string;
	deviceName: string;
}

function getCallOutDevicePreference(): CallOutDevicePreference | null {
	const { deviceId, deviceName } = usePreferencesStore.getState().ringCentral;

	if (deviceId == null) {
		return null;
	}

	return {
		deviceId,
		deviceName: deviceName ?? 'Unknown Device',
	};
}

function setCallOutDevicePreference(
	preference: CallOutDevicePreference | null,
): void {
	if (preference == null) {
		usePreferencesStore.getState().setRingCentralPreferences({
			deviceId: null,
			deviceName: null,
		});
	} else {
		usePreferencesStore.getState().setRingCentralPreferences({
			deviceId: preference.deviceId,
			deviceName: preference.deviceName,
		});
	}
}

function getRingOutPhonePreference(): string | null {
	return usePreferencesStore.getState().ringCentral.ringOutPhoneNumber;
}

function setRingOutPhonePreference(phoneNumber: string | null): void {
	usePreferencesStore
		.getState()
		.setRingCentralPreference('ringOutPhoneNumber', phoneNumber);
}

// ========================================
// Vonage store preference functions
// ========================================

type VonageCallMethodPreference = 'vonage' | 'phone_app' | null;

function getVonageCallMethodPreference(): VonageCallMethodPreference {
	const stored = usePreferencesStore.getState().vonage.callMethodPreference;

	if (stored === 'vonage' || stored === 'phone_app') {
		return stored;
	}

	return null;
}

function setVonageCallMethodPreference(
	preference: VonageCallMethodPreference,
): void {
	usePreferencesStore
		.getState()
		.setVonagePreference('callMethodPreference', preference);
}

// ========================================
// Reliable PBX store preference functions
// ========================================

type ReliablePbxCallMethodPreference = 'reliablepbx' | 'phone_app' | null;

function getReliablePbxCallMethodPreference(): ReliablePbxCallMethodPreference {
	const stored =
		usePreferencesStore.getState().reliablePbx.callMethodPreference;

	if (stored === 'reliablepbx' || stored === 'phone_app') {
		return stored;
	}

	return null;
}

function setReliablePbxCallMethodPreference(
	preference: ReliablePbxCallMethodPreference,
): void {
	usePreferencesStore
		.getState()
		.setReliablePbxPreference('callMethodPreference', preference);
}

// ========================================
// Component
// ========================================

interface ClickToDialPhoneProps {
	phoneNumber: string | null | undefined;
	className?: string;
	showIcon?: boolean;
	/**
	 * The CRM record this number was clicked ON (PD-14414). When set,
	 * softphone dials show "who you're talking to" in the active-call
	 * bar immediately ("Jane Doe — Acme Corp") and the backend stamps
	 * the call's primary attribution from the rep's intent instead of
	 * a phone-map guess. Optional — surfaces without a single obvious
	 * record (e.g. generic lists) omit it and keep matcher behavior.
	 */
	crmContext?: SoftphoneDialCrmContextModel;
}

/**
 * Phone number display component with click-to-dial capability.
 *
 * If an integration (RingCentral or Vonage) is connected at the instance level:
 * - Prompts user to choose between the available integrations and phone app
 * - If user chooses an integration and needs authentication/setup, initiates that flow
 * - Remembers user's choices if they check the "remember" checkbox
 *
 * If no integration is connected:
 * - Falls back to a standard tel: link
 */
export function ClickToDialPhone({
	phoneNumber,
	className = '',
	showIcon = false,
	crmContext,
}: ClickToDialPhoneProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const ringCentral = useRingCentralSafe();
	const vonage = useVonageSafe();
	const softphone = useSoftphoneSafe();
	const reliablePbx = useReliablePbxSafe();

	// Modal visibility states
	const [showMethodModal, setShowMethodModal] = useState(false);
	const [showRingCentralAuthModal, setShowRingCentralAuthModal] =
		useState(false);
	const [showRingCentralDialModal, setShowRingCentralDialModal] =
		useState(false);
	const [showVonageExtensionSetup, setShowVonageExtensionSetup] =
		useState(false);
	const [pendingDial, setPendingDial] = useState(false);

	// RingCentral status
	const rcDialStatus = ringCentral?.dialStatus;
	const rcIsConnected = rcDialStatus?.connected ?? false;
	const rcIsUserAuthenticated = rcDialStatus?.userAuthenticated ?? false;
	const rcNeedsUserAuth = rcDialStatus?.needsUserAuth ?? false;
	const rcCanDial = rcDialStatus?.canDial ?? false;
	const rcIsDialing = ringCentral?.isDialing ?? false;

	// Vonage status
	const vonageDialStatus = vonage?.dialStatus;
	const vonageIsConnected = vonageDialStatus?.connected ?? false;
	const vonageCanDial = vonageDialStatus?.canDial ?? false;
	const vonageIsDialing = vonage?.isDialing ?? false;

	// Softphone status — registered+leader means this tab actually
	// holds the device handle and can dial. Follower tabs see the
	// softphone as "available in another tab" and the call pop's
	// `<CallActiveInOtherTabBanner>` handles handoff.
	const softphoneIsRegistered = softphone?.isRegistered ?? false;
	const softphoneIsLeader = softphone?.isLeader ?? false;
	const softphoneAvailable = softphoneIsRegistered && softphoneIsLeader;

	// User's default outbound caller-ID (DD.9.1). Resolved once the
	// softphone is available (and re-resolves if availability flips).
	// The dial endpoint validates that this number is assigned to the
	// caller; if the lookup fails for any reason we just dial without
	// it and let the backend pick the user's default server-side.
	const [defaultOutboundE164, setDefaultOutboundE164] = useState<string | null>(
		null,
	);
	useEffect(() => {
		if (!softphoneAvailable) return;
		let cancelled = false;
		void SoftphoneSelfServiceQueryService.listMyNumbers()
			.then((res) => {
				if (cancelled) return;
				const def =
					res.numbers.find((n) => n.isDefaultOutbound) ?? res.numbers[0];
				setDefaultOutboundE164(def?.phoneE164 ?? null);
			})
			.catch(() => {
				/* fall back to server-side default */
			});
		return () => {
			cancelled = true;
		};
	}, [softphoneAvailable]);

	// Reliable PBX status
	const reliablePbxDialStatus = reliablePbx?.dialStatus;
	const reliablePbxIsConnected = reliablePbxDialStatus?.connected ?? false;
	const reliablePbxCanDial = reliablePbxDialStatus?.canDial ?? false;
	const reliablePbxIsDialing = reliablePbx?.isDialing ?? false;

	// Softphone dialing state. Declared here — above the combined
	// `isDialing` below that reads it — so the read isn't a
	// temporal-dead-zone access (this `const` was previously declared
	// further down, next to the dial function, which crashed the
	// component on render). The dial function that flips it still lives
	// below.
	const [softphoneDialing, setSoftphoneDialing] = useState(false);

	// Combined dialing state — covers all integrations so the
	// click-to-dial button disables uniformly while any dial is in
	// flight.
	const isDialing =
		rcIsDialing || vonageIsDialing || softphoneDialing || reliablePbxIsDialing;

	// Determine which integrations are available
	const availableIntegrations = useMemo(() => {
		const integrations: Array<CallMethodIntegration> = [];

		// The softphone only registers on a tenant holding the softphone
		// capability (the shell's flag), so it needs no check here.
		if (softphoneAvailable) {
			integrations.push('softphone');
		}

		// PD-16917 — click-to-dial through a connected phone system is part
		// of the phone capability. Without it the number is a plain tel: link.
		const phoneAllowed = CapabilityService.allows(Capabilities.PHONE);

		if (phoneAllowed && vonageIsConnected) {
			integrations.push('vonage');
		}

		if (phoneAllowed && rcIsConnected) {
			integrations.push('ringcentral');
		}

		if (phoneAllowed && reliablePbxIsConnected) {
			integrations.push('reliablepbx');
		}

		return integrations;
	}, [
		softphoneAvailable,
		vonageIsConnected,
		rcIsConnected,
		reliablePbxIsConnected,
	]);

	const hasAnyIntegration = availableIntegrations.length > 0;

	// Memoize phone formatting
	const formattedPhone = useMemo(() => {
		if (phoneNumber == null || phoneNumber.trim() === '') {
			return '';
		}

		return formatPhoneNumber(phoneNumber);
	}, [phoneNumber]);

	const cleanedPhoneNumber = useMemo(() => {
		if (phoneNumber == null) {
			return '';
		}

		return phoneNumber.replace(/\D/g, '');
	}, [phoneNumber]);

	const openPhoneApp = useCallback((): void => {
		window.location.href = `tel:${cleanedPhoneNumber}`;
	}, [cleanedPhoneNumber]);

	// ========================================
	// Softphone dial function
	// ========================================

	const dialWithSoftphone = useCallback(async (): Promise<void> => {
		if (softphone == null || phoneNumber == null) {
			return;
		}
		if (softphoneDialing) {
			// Rapid-click guard — suppress duplicate dials while a
			// previous attempt is in flight. The button is also
			// `disabled` via `isDialing`, but stop the dispatch
			// defensively in case the click slips through.
			return;
		}
		if (softphone.currentCall != null) {
			Toast.show({
				type: 'error',
				text1: t('Already on a call'),
				text2: t('End the current call before starting a new one.'),
			});
			return;
		}
		if (!softphone.isRegistered) {
			Toast.show({
				type: 'error',
				text1: t('Softphone is not registered yet'),
				text2: t('Wait a moment for the device to come online and try again.'),
			});
			return;
		}
		setSoftphoneDialing(true);
		try {
			await softphone.dial(phoneNumber, {
				fromCallerId: defaultOutboundE164 ?? undefined,
				crmContext,
			});
			Toast.show({
				type: 'success',
				text1: t('Calling via softphone...'),
			});
		} catch (err) {
			Toast.show({
				type: 'error',
				text1: t('Failed to start call'),
				text2: err instanceof Error ? t(err.message) : undefined,
			});
		} finally {
			setSoftphoneDialing(false);
		}
	}, [
		softphone,
		phoneNumber,
		t,
		softphoneDialing,
		defaultOutboundE164,
		crmContext,
	]);

	const handleSelectSoftphone = useCallback((): void => {
		void dialWithSoftphone();
	}, [dialWithSoftphone]);

	// ========================================
	// Vonage dial functions
	// ========================================

	const dialWithVonage = useCallback(async (): Promise<void> => {
		if (vonage == null || phoneNumber == null) {
			return;
		}

		const result = await vonage.dial(phoneNumber);

		if (result.success) {
			if (result.diverted === true) {
				Toast.show({
					type: 'info',
					text1: t('Call diverted'),
					text2: t(
						`Calling ${result.divertedTo ?? ''} instead of ${result.originalNumber ?? ''}`,
					),
				});
			} else {
				Toast.show({
					type: 'success',
					text1: t('Initiating call via Vonage...'),
				});
			}
		} else {
			Toast.show({
				type: 'error',
				text1: t('Failed to initiate call'),
				text2: result.error != null ? t(result.error) : undefined,
			});
		}
	}, [vonage, phoneNumber, t]);

	const handleSelectVonage = useCallback(
		(remember: boolean): void => {
			if (remember) {
				setVonageCallMethodPreference('vonage');
			}

			// Check if user can dial (has extension mapped)
			// Use both canDial AND userExtension check for robustness
			const hasExtension =
				vonageDialStatus?.userExtension != null &&
				vonageDialStatus.userExtension.trim() !== '';

			if (vonageCanDial && hasExtension) {
				void dialWithVonage();
			} else {
				// User needs to set up their extension
				setPendingDial(true);
				setShowVonageExtensionSetup(true);
			}
		},
		[vonageCanDial, vonageDialStatus?.userExtension, dialWithVonage],
	);

	const handleVonageExtensionSetupComplete = useCallback((): void => {
		// Refresh the Vonage status
		if (vonage != null) {
			void vonage.refreshStatus();
		}

		if (pendingDial) {
			setPendingDial(false);
			// After setup is complete, dial
			void dialWithVonage();
		}
	}, [vonage, pendingDial, dialWithVonage]);

	// ========================================
	// Reliable PBX dial functions
	// ========================================

	const dialWithReliablePbx = useCallback(async (): Promise<void> => {
		if (reliablePbx == null || phoneNumber == null) {
			return;
		}

		const result = await reliablePbx.dial(phoneNumber);

		if (result.success) {
			if (result.diverted === true) {
				Toast.show({
					type: 'info',
					text1: t('Call diverted'),
					text2: t(
						`Calling ${result.divertedTo ?? ''} instead of ${result.originalNumber ?? ''}`,
					),
				});
			} else {
				Toast.show({
					type: 'success',
					text1: t('Initiating call via Reliable PBX...'),
				});
			}
		} else {
			Toast.show({
				type: 'error',
				text1: t('Failed to initiate call'),
				text2: result.error != null ? t(result.error) : undefined,
			});
		}
	}, [reliablePbx, phoneNumber, t]);

	// Users cannot self-assign a Reliable PBX extension (no discovery API), so if
	// they can't dial we show an error directing them to an admin — no setup modal.
	const handleSelectReliablePbx = useCallback(
		(remember: boolean): void => {
			if (remember) {
				setReliablePbxCallMethodPreference('reliablepbx');
			}

			if (reliablePbxCanDial) {
				void dialWithReliablePbx();
			} else {
				Toast.show({
					type: 'error',
					text1: t('Unable to dial'),
					text2: t(
						'You need a Reliable PBX extension assigned. Please contact your administrator.',
					),
				});
			}
		},
		[reliablePbxCanDial, dialWithReliablePbx, t],
	);

	// ========================================
	// RingCentral dial functions
	// ========================================

	// A failed RingCentral dial shows its error, then steers the user to the
	// fix: reconnect an expired sign-in, or reopen the dialog when the phone or
	// number it used cannot work. A remembered phone RingCentral no longer knows
	// is forgotten so the next click does not fail the same way (PD-16786).
	const handleRingCentralDialFailure = useCallback(
		(result: RingCentralDialResponseDto): void => {
			Toast.show({
				type: 'error',
				text1: t('Failed to initiate call'),
				text2: result.error != null ? t(result.error) : undefined,
			});

			const action = getDialFailureAction(result.errorCode);

			if (action.forgetDevice) {
				setCallOutDevicePreference(null);

				if (getDialMethodPreference() === 'callout') {
					setDialMethodPreference(null);
				}
			}

			// The sign-in dialog refreshes the dial status once the user is
			// connected again (handleRingCentralAuthComplete). Refreshing here
			// would mark the user as unable to dial before that, and the pending
			// call would then be refused.
			if (action.reconnect) {
				setPendingDial(true);
				setShowRingCentralAuthModal(true);
			} else if (action.reopenDialog) {
				setShowRingCentralDialModal(true);
			}
		},
		[t],
	);

	// Dial via RingCentral with CallOut (device-based)
	const dialWithCallOut = useCallback(
		async (deviceId: string): Promise<void> => {
			if (ringCentral == null || phoneNumber == null) {
				return;
			}

			const result = await ringCentral.dial(phoneNumber, {
				callMethod: 'callout',
				deviceId,
			});

			if (result.success) {
				if (result.diverted === true) {
					Toast.show({
						type: 'info',
						text1: t('Call diverted'),
						text2: t(
							`Calling ${result.divertedTo ?? ''} instead of ${result.originalNumber ?? ''}`,
						),
					});
				} else {
					Toast.show({
						type: 'success',
						text1: t('Initiating call...'),
					});
				}
			} else {
				handleRingCentralDialFailure(result);
			}
		},
		[ringCentral, phoneNumber, t, handleRingCentralDialFailure],
	);

	// Dial via RingCentral with RingOut (phone number-based)
	const dialWithRingOut = useCallback(
		async (ringOutPhone: string): Promise<void> => {
			if (ringCentral == null || phoneNumber == null) {
				return;
			}

			const result = await ringCentral.dial(phoneNumber, {
				callMethod: 'ringout',
				ringOutPhoneNumber: ringOutPhone,
			});

			if (result.success) {
				if (result.diverted === true) {
					Toast.show({
						type: 'info',
						text1: t('Call diverted'),
						text2: t(
							`Calling ${result.divertedTo ?? ''} instead of ${result.originalNumber ?? ''}`,
						),
					});
				} else {
					Toast.show({
						type: 'success',
						text1: t('Initiating call...'),
						text2: t('RingCentral will call you first, then connect you.'),
					});
				}
			} else {
				handleRingCentralDialFailure(result);
			}
		},
		[ringCentral, phoneNumber, t, handleRingCentralDialFailure],
	);

	// Check if we have all saved preferences to dial immediately
	const tryDialWithRingCentralPreferences = useCallback((): boolean => {
		const dialMethod = getDialMethodPreference();

		if (dialMethod === 'callout') {
			const devicePref = getCallOutDevicePreference();

			if (devicePref != null) {
				void dialWithCallOut(devicePref.deviceId);

				return true;
			}
		} else if (dialMethod === 'ringout') {
			const ringOutPhone = getRingOutPhonePreference();

			if (ringOutPhone != null) {
				void dialWithRingOut(ringOutPhone);

				return true;
			}
		}

		return false;
	}, [dialWithCallOut, dialWithRingOut]);

	// Handle user selecting RingCentral from the initial call method modal
	const handleSelectRingCentral = useCallback(
		(remember: boolean): void => {
			if (remember) {
				setRingCentralCallMethodPreference('ringcentral');
			}

			// Check if user needs to authenticate
			if (rcNeedsUserAuth && !rcIsUserAuthenticated) {
				setPendingDial(true);
				setShowRingCentralAuthModal(true);
			} else {
				// Try to dial with saved preferences, otherwise show dial modal
				if (!tryDialWithRingCentralPreferences()) {
					setShowRingCentralDialModal(true);
				}
			}
		},
		[rcNeedsUserAuth, rcIsUserAuthenticated, tryDialWithRingCentralPreferences],
	);

	// Handle user selecting phone app
	const handleSelectPhoneApp = useCallback(
		(remember: boolean): void => {
			// Save preference based on which integrations are available
			if (remember) {
				if (vonageIsConnected && !rcIsConnected) {
					setVonageCallMethodPreference('phone_app');
				} else if (rcIsConnected) {
					setRingCentralCallMethodPreference('phone_app');
				}
			}

			openPhoneApp();
		},
		[openPhoneApp, vonageIsConnected, rcIsConnected],
	);

	// Handle unified call method selection
	const handleMethodSelected = useCallback(
		(method: CallMethodType, remember: boolean): void => {
			setShowMethodModal(false);

			if (method === 'softphone') {
				// Softphone preference is per-call in v1 — no persistence yet.
				// `remember` is ignored here intentionally.
				void remember;
				handleSelectSoftphone();
			} else if (method === 'vonage') {
				handleSelectVonage(remember);
			} else if (method === 'ringcentral') {
				handleSelectRingCentral(remember);
			} else if (method === 'reliablepbx') {
				handleSelectReliablePbx(remember);
			} else {
				handleSelectPhoneApp(remember);
			}
		},
		[
			handleSelectSoftphone,
			handleSelectVonage,
			handleSelectRingCentral,
			handleSelectReliablePbx,
			handleSelectPhoneApp,
		],
	);

	// Handle RingCentral auth completion
	const handleRingCentralAuthComplete = useCallback((): void => {
		if (ringCentral != null) {
			void ringCentral.refreshStatus();
		}

		if (pendingDial) {
			setPendingDial(false);

			// Try to dial with saved preferences, otherwise show dial modal
			if (!tryDialWithRingCentralPreferences()) {
				setShowRingCentralDialModal(true);
			}
		}
	}, [ringCentral, pendingDial, tryDialWithRingCentralPreferences]);

	// The dial dialog found the RingCentral sign-in expired: swap it for the
	// sign-in dialog. Once signed in, handleRingCentralAuthComplete refreshes
	// the dial status and continues the pending call.
	const handleRingCentralReconnect = useCallback((): void => {
		setShowRingCentralDialModal(false);
		setPendingDial(true);
		setShowRingCentralAuthModal(true);
	}, []);

	// Handle dial modal selection
	const handleDialSelected = useCallback(
		(result: RingCentralDialResult): void => {
			setShowRingCentralDialModal(false);

			if (result.remember) {
				setDialMethodPreference(result.dialMethod);

				if (result.dialMethod === 'callout' && result.deviceId != null) {
					setCallOutDevicePreference({
						deviceId: result.deviceId,
						deviceName: result.deviceName ?? 'Unknown Device',
					});
				} else if (
					result.dialMethod === 'ringout' &&
					result.ringOutPhoneNumber != null
				) {
					setRingOutPhonePreference(result.ringOutPhoneNumber);
				}
			}

			if (result.dialMethod === 'callout' && result.deviceId != null) {
				void dialWithCallOut(result.deviceId);
			} else if (
				result.dialMethod === 'ringout' &&
				result.ringOutPhoneNumber != null
			) {
				void dialWithRingOut(result.ringOutPhoneNumber);
			}
		},
		[dialWithCallOut, dialWithRingOut],
	);

	// Main click handler
	const handleClick = useCallback(
		(e: React.MouseEvent): void => {
			e.preventDefault();
			e.stopPropagation();

			// If no integrations connected, just use tel: link
			if (!hasAnyIntegration) {
				openPhoneApp();

				return;
			}

			// Single-integration shortcut: softphone alone dials directly.
			// Pilot tenants with only Twilio see no modal at all — just
			// click and the call rings their browser. Multi-integration
			// tenants fall through to the modal below.
			if (
				availableIntegrations.length === 1 &&
				availableIntegrations[0] === 'softphone'
			) {
				void dialWithSoftphone();
				return;
			}

			// Check for stored preferences
			// First check Vonage preference if Vonage is connected
			if (vonageIsConnected) {
				const vonagePref = getVonageCallMethodPreference();

				if (vonagePref === 'phone_app') {
					openPhoneApp();

					return;
				}

				if (vonagePref === 'vonage') {
					const hasExtension =
						vonageDialStatus?.userExtension != null &&
						vonageDialStatus.userExtension.trim() !== '';

					if (vonageCanDial && hasExtension) {
						void dialWithVonage();
					} else {
						// User needs to set up extension
						setPendingDial(true);
						setShowVonageExtensionSetup(true);
					}

					return;
				}
			}

			// Check RingCentral preference if RingCentral is connected
			if (rcIsConnected) {
				const rcPref = getRingCentralCallMethodPreference();

				if (rcPref === 'phone_app') {
					openPhoneApp();

					return;
				}

				if (rcPref === 'ringcentral') {
					// Check if user needs to authenticate
					if (rcNeedsUserAuth && !rcIsUserAuthenticated) {
						setPendingDial(true);
						setShowRingCentralAuthModal(true);
					} else if (rcCanDial) {
						// Try to dial with saved preferences, otherwise show dial modal
						if (!tryDialWithRingCentralPreferences()) {
							setShowRingCentralDialModal(true);
						}
					} else {
						Toast.show({
							type: 'error',
							text1: t(
								'Unable to dial. Please check your RingCentral configuration.',
							),
						});
					}

					return;
				}
			}

			// Check Reliable PBX preference if Reliable PBX is connected
			if (reliablePbxIsConnected) {
				const reliablePbxPref = getReliablePbxCallMethodPreference();

				if (reliablePbxPref === 'phone_app') {
					openPhoneApp();

					return;
				}

				if (reliablePbxPref === 'reliablepbx') {
					if (reliablePbxCanDial) {
						void dialWithReliablePbx();
					} else {
						Toast.show({
							type: 'error',
							text1: t('Unable to dial'),
							text2: t(
								'You need a Reliable PBX extension assigned. Please contact your administrator.',
							),
						});
					}

					return;
				}
			}

			// No preference set - show the method selection modal
			setShowMethodModal(true);
		},
		[
			hasAnyIntegration,
			availableIntegrations,
			dialWithSoftphone,
			vonageIsConnected,
			vonageCanDial,
			vonageDialStatus?.userExtension,
			rcIsConnected,
			rcNeedsUserAuth,
			rcIsUserAuthenticated,
			rcCanDial,
			reliablePbxIsConnected,
			reliablePbxCanDial,
			openPhoneApp,
			dialWithVonage,
			dialWithReliablePbx,
			tryDialWithRingCentralPreferences,
			t,
		],
	);

	// Handle empty/null phone numbers - render early but after all hooks
	if (phoneNumber == null || phoneNumber.trim() === '') {
		return (
			<span
				className={className}
				style={{ color: colors.values.textSecondary }}
			>
				{t('-')}
			</span>
		);
	}

	// If no integrations connected, show simple tel: link
	if (!hasAnyIntegration) {
		return (
			<a
				href={`tel:${cleanedPhoneNumber}`}
				className={`hover:underline transition-all inline-block ${className}`}
				style={{ color: colors.values.link }}
				onClick={(e) => {
					e.stopPropagation();
				}}
			>
				{t(formattedPhone)}
			</a>
		);
	}

	// Instance has integrations connected - render as clickable button with modals
	return (
		<>
			<button
				onClick={handleClick}
				disabled={isDialing}
				className={`hover:underline transition-all inline-flex items-center gap-1 cursor-pointer disabled:opacity-50 ${className}`}
				style={{ color: colors.values.link }}
				title={t('Click to call')}
				type="button"
			>
				{showIcon ? (
					<FontAwesomeIcon icon={faPhone} className="text-xs" />
				) : null}
				{t(formattedPhone)}
			</button>

			{/* Step 1: Choose between integrations and phone app */}
			<CallMethodModal
				isOpen={showMethodModal}
				onClose={() => {
					setShowMethodModal(false);
				}}
				phoneNumber={formattedPhone}
				availableIntegrations={availableIntegrations}
				onSelectMethod={handleMethodSelected}
			/>

			{/* Vonage extension setup modal */}
			<VonageExtensionSetupModal
				isOpen={showVonageExtensionSetup}
				onClose={() => {
					setShowVonageExtensionSetup(false);
					setPendingDial(false);
				}}
				onSetupComplete={handleVonageExtensionSetupComplete}
			/>

			{/* RingCentral user authentication */}
			<RingCentralUserAuthModal
				isOpen={showRingCentralAuthModal}
				onClose={() => {
					setShowRingCentralAuthModal(false);
					setPendingDial(false);
				}}
				onAuthComplete={handleRingCentralAuthComplete}
			/>

			{/* RingCentral dial modal (CallOut/RingOut + configuration). No
			    remembered method means automatic: CallOut when a phone can be
			    rung right now, RingOut otherwise (PD-16786). */}
			<RingCentralDialModal
				isOpen={showRingCentralDialModal}
				onClose={() => {
					setShowRingCentralDialModal(false);
				}}
				onSelect={handleDialSelected}
				onReconnect={handleRingCentralReconnect}
				initialDialMethod={getDialMethodPreference()}
				initialDeviceId={getCallOutDevicePreference()?.deviceId ?? null}
				initialRingOutPhone={getInitialRingOutPhone(
					getRingOutPhonePreference(),
					rcDialStatus?.userPhoneNumber ?? null,
				)}
			/>
		</>
	);
}
